import React, { useEffect, useRef, useState } from 'react';
import { useMemberApp } from '../AppProvider';
import { memberApi } from '../api';
import { getAuthToken } from '@/utils/auth';

export default function DirectMessages() {
  const { user } = useMemberApp();

  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [textInput, setTextString] = useState('');

  // Sockets & short pollers
  const socketRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const isWsConnectedRef = useRef(false);
  const chatBottomRef = useRef(null);

  const [loadingConv, setLoadingConv] = useState(true);
  const [loadingChat, setLoadingChat] = useState(false);
  const [wsStatus, setWsStatus] = useState('connecting'); // connecting | online | offline (polling fallback)

  useEffect(() => {
    loadActiveConversations();
    return () => {
      disconnectWebSocket();
      stopPollingFallback();
    };
  }, []);

  useEffect(() => {
    if (selectedUser) {
      loadMessageHistory(selectedUser.other_user_id);
    }
  }, [selectedUser]);

  useEffect(() => {
    // Scroll to bottom on new messages
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 1. Fetch conversations directory
  const loadActiveConversations = async () => {
    try {
      setLoadingConv(true);
      const list = await memberApi.communityConversations();
      setConversations(list);
      
      // Auto-select first thread if present
      if (list.length > 0 && !selectedUser) {
        setSelectedUser(list[0]);
      }
    } catch (err) {
      console.error('Failed to load conversations', err);
    } finally {
      setLoadingConv(false);
    }
  };

  // 2. Load messages with target
  const loadMessageHistory = async (otherUserId) => {
    try {
      setLoadingChat(true);
      const history = await memberApi.communityMessageHistory(otherUserId);
      setMessages(history);

      // Reconnect/Ensure Sockets are wired up
      setupWebSocket(otherUserId);
    } catch (err) {
      console.error('Failed to fetch message logs', err);
    } finally {
      setLoadingChat(false);
    }
  };

  // 3. Setup real-time WebSockets
  const setupWebSocket = (otherUserId) => {
    disconnectWebSocket();
    stopPollingFallback();

    if (!user?.id) return;

    try {
      // Build location-agnostic websocket path matching FastAPI's server address
      const loc = window.location;
      const wsProtocol = loc.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = process.env.NODE_ENV === 'production' ? loc.host : 'localhost:8000';
      const wsUrl = `${wsProtocol}//${wsHost}/api/community/chat/ws/${user.id}`;

      console.log(`Connecting community WebSocket: ${wsUrl}`);
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('Community WebSocket is connected and online.');
        isWsConnectedRef.current = true;
        setWsStatus('online');
      };

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          
          if (payload.type === 'new_message' && payload.message) {
            const msg = payload.message;
            // Append message if it belongs to active chat
            if (msg.sender_id === otherUserId || msg.recipient_id === otherUserId) {
              setMessages((prev) => {
                if (prev.some((m) => m.id === msg.id)) return prev;
                return [...prev, msg];
              });
            }
            // Reload sidebar list to show unread counters / last message
            loadActiveConversations();
          }

          if (payload.type === 'message_sent' && payload.message) {
            const msg = payload.message;
            setMessages((prev) => {
              if (prev.some((m) => m.id === msg.id)) return prev;
              return [...prev, msg];
            });
          }
        } catch (err) {
          console.error('Failed parsing WS incoming payload', err);
        }
      };

      socket.onclose = () => {
        console.warn('WebSocket closed. Launching REST short-polling fallback.');
        handleWebSocketFailure(otherUserId);
      };

      socket.onerror = () => {
        console.error('WebSocket encountered an error.');
        handleWebSocketFailure(otherUserId);
      };

    } catch (err) {
      console.error('Failed opening WebSocket', err);
      handleWebSocketFailure(otherUserId);
    }
  };

  const handleWebSocketFailure = (otherUserId) => {
    isWsConnectedRef.current = false;
    setWsStatus('offline');
    startPollingFallback(otherUserId);
  };

  const disconnectWebSocket = () => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    isWsConnectedRef.current = false;
  };

  // 4. REST Polling Fallback (Every 3 seconds)
  const startPollingFallback = (otherUserId) => {
    stopPollingFallback();
    console.log(`Starting 3s short-poll fallback for user: ${otherUserId}`);
    pollIntervalRef.current = setInterval(async () => {
      try {
        const history = await memberApi.communityMessageHistory(otherUserId);
        setMessages(history);
      } catch (err) {
        console.error('Polling error', err);
      }
    }, 3000);
  };

  const stopPollingFallback = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  // 5. Send Message (WS dispatch with HTTP fallback)
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!textInput.trim() || !selectedUser) return;

    const body = {
      recipient_id: selectedUser.other_user_id,
      text: textInput.trim(),
    };

    const textToSubmit = textInput.trim();
    setTextString('');

    // If WebSocket is active, send over socket
    if (isWsConnectedRef.current && socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      try {
        socketRef.current.send(JSON.stringify(body));
        return;
      } catch (err) {
        console.error('Failed sending over WebSocket, trying REST', err);
      }
    }

    // REST HTTP fallback send
    try {
      const result = await memberApi.sendCommunityMessage(body);
      setMessages((prev) => [...prev, result]);
      loadActiveConversations();
    } catch (err) {
      alert('Could not deliver message. Please try again.');
    }
  };

  return (
    <div className="by-stage max-w-6xl mx-auto px-4 py-8 h-[calc(100vh-120px)] flex flex-col">
      <div className="flex-1 bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden grid grid-cols-1 md:grid-cols-3">
        {/* 1. Left Column: Conversations Directory */}
        <div className="border-r border-slate-100 flex flex-col bg-slate-50/50">
          <header className="p-4 border-b border-slate-100 bg-white flex items-center justify-between">
            <h1 className="text-base font-bold text-slate-900">Direct Messages</h1>
            <span className={`text-[10px] font-extrabold uppercase px-2.5 py-1 rounded-full border ${
              wsStatus === 'online' 
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                : 'bg-amber-50 text-amber-700 border-amber-200'
            }`}>
              {wsStatus === 'online' ? '● Live' : '🔌 Polling'}
            </span>
          </header>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {loadingConv ? (
              <div className="text-center py-8 text-xs text-slate-400">Loading inbox threads...</div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-12 text-xs text-slate-400 px-4 leading-relaxed">
                Your direct messages index is empty. Visit the community feed and tap on members to start chatting!
              </div>
            ) : (
              conversations.map((c) => {
                const active = selectedUser?.other_user_id === c.other_user_id;
                return (
                  <button
                    key={c.other_user_id}
                    type="button"
                    onClick={() => setSelectedUser(c)}
                    className={`w-full text-left p-3.5 rounded-xl flex items-center gap-3 transition ${
                      active ? 'bg-white shadow-sm border border-slate-100 border-l-4 border-l-indigo-600' : 'hover:bg-slate-100/50 border border-transparent'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center font-bold text-indigo-700 text-sm flex-shrink-0">
                      {c.avatar ? (
                        <img src={c.avatar} alt={c.display_name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        c.display_name[0]
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-900 truncate">{c.display_name}</span>
                        {c.unread_count > 0 && (
                          <span className="bg-rose-500 text-white font-extrabold text-[10px] h-4 min-w-4 px-1 rounded-full flex items-center justify-center">
                            {c.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-400 truncate mt-0.5">{c.last_message || 'Start messaging...'}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* 2. Right Area: Chat History & Input */}
        <div className="md:col-span-2 flex flex-col bg-white">
          {selectedUser ? (
            <>
              {/* Chat Header */}
              <header className="p-4 border-b border-slate-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center font-bold text-indigo-700">
                  {selectedUser.avatar ? (
                    <img src={selectedUser.avatar} alt={selectedUser.display_name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    selectedUser.display_name[0]
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-bold text-slate-900">{selectedUser.display_name}</span>
                    {selectedUser.badges?.map((b) => (
                      <span key={b} className="bg-indigo-50 text-indigo-700 text-[9px] font-bold px-1.5 py-0.5 rounded">
                        {b}
                      </span>
                    ))}
                  </div>
                  <span className="text-[10px] text-slate-400">Cancer Care Peer Connection</span>
                </div>
              </header>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/30">
                {loadingChat ? (
                  <div className="text-center py-8 text-xs text-slate-400">Loading conversation history...</div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-12 text-xs text-slate-400 leading-relaxed">
                    Say hello to {selectedUser.display_name}! This channel is encrypted, secure, and private.
                  </div>
                ) : (
                  messages.map((m) => {
                    const isMe = m.sender_id === user?.id;
                    return (
                      <div key={m.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`p-3.5 rounded-2xl max-w-sm text-xs leading-relaxed ${
                          isMe 
                            ? 'bg-indigo-600 text-white rounded-br-none shadow-sm' 
                            : 'bg-white text-slate-800 rounded-bl-none border border-slate-100 shadow-sm'
                        }`}>
                          <p className="whitespace-pre-wrap">{m.text}</p>
                          <span className={`text-[9px] block text-right mt-1.5 ${isMe ? 'text-indigo-200' : 'text-slate-400'}`}>
                            {new Date(m.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={chatBottomRef} />
              </div>

              {/* Chat Composer */}
              <footer className="p-4 border-t border-slate-100 bg-white">
                <form onSubmit={handleSendMessage} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={textInput}
                    onChange={(e) => setTextString(e.target.value)}
                    placeholder={`Write your message to ${selectedUser.display_name}...`}
                    className="flex-1 px-4 py-3 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  <button
                    type="submit"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-5 py-3 rounded-xl text-xs transition"
                  >
                    Send
                  </button>
                </form>
              </footer>
            </>
          ) : (
            <div className="flex-1 flex flex-col justify-center items-center text-slate-400 text-xs py-12">
              <span className="text-3xl mb-2">✉</span>
              Select or start a chat from the contacts sidebar.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
